/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Claim State Utils
 */
import { CLAIM_WORK_STATES } from 'app-constants/claims/claimRequest';
import _ from 'lodash';

const CLAIM_STATES = {
	processing: CLAIM_WORK_STATES.a_processing.value,
	infoReq: CLAIM_WORK_STATES.a_info_req.value,
	adjudicated: CLAIM_WORK_STATES.a_adjudicated.value,
	paymentPosted: CLAIM_WORK_STATES.a_payment_posted.value,
	notAccepted: CLAIM_WORK_STATES.ia_not_accepted.value,
	void: CLAIM_WORK_STATES.ia_void.value,
	archived: CLAIM_WORK_STATES.archived.value,
	closed: CLAIM_WORK_STATES.ia_closed.value,
	superseded: CLAIM_WORK_STATES.ia_superseded.value,
	voidSuperseded: CLAIM_WORK_STATES.ia_void_superseded.value,
};

type ActionState = {
	disabled?: boolean;
	visible?: boolean;
	tooltip?: string | undefined;
};

type PostPaymentActionState = ActionState & {
	location?: 'page' | 'dropdown';
};

type GetActionsParams = {
	workState: ClaimRequest.IClaimWorkState;
	pendingPR?: number;
	hasManualClaim?: boolean;
	isDraftPostAvailable?: boolean;
	userAccess?: App.UserAccess;
};

type Actions = {
	changeWorkState: ActionState;
	resubmitClaim: ActionState;
	postPayment: PostPaymentActionState;
	addActivity: ActionState;
	modifyClaimWorklist: ActionState;
	refreshClaimStatus: ActionState;
	allActions: ActionState;
	fileSecondaryClaim: ActionState;
};

const getChangeWorkState = (workState: ClaimRequest.IClaimWorkState, pendingPR?: number, isDraftPostAvailable?: boolean): ActionState => {
	if (workState === CLAIM_STATES.closed) {
		return { visible: true, disabled: false };
	}
	if (isDraftPostAvailable) {
		return { disabled: true, tooltip: 'This claim cannot be closed until the draft is resolved.', visible: true };
	}
	if (_.includes([CLAIM_STATES.processing, CLAIM_STATES.infoReq, CLAIM_STATES.adjudicated], workState)) {
		return { disabled: true, tooltip: 'This claim cannot be closed as adjudication is in process.', visible: true };
	}
	if (workState === CLAIM_STATES.paymentPosted) {
		if (pendingPR && pendingPR > 0) {
			return { disabled: true, tooltip: 'This claim cannot be closed as there is a Pending PR.', visible: true };
		}
		return { disabled: false, visible: true };
	}
	return { visible: false };
};

// resubmit wont be shown for these states and if its secondary has been filed
const ALLOWED_RESUBMIT_STATES: ClaimRequest.IClaimWorkState[] = [CLAIM_STATES.adjudicated, CLAIM_STATES.paymentPosted, CLAIM_STATES.closed];

const ALLOWED_SECONDARY_FILING_STATES: ClaimRequest.IClaimWorkState[] = [CLAIM_STATES.paymentPosted, CLAIM_STATES.closed];

const REOPEN_CLAIM_TOOLTIP = 'Reopen the claim to proceed with this action.';

const getResubmitClaim = (workState: ClaimRequest.IClaimWorkState): ActionState => {
	if (!_.includes(ALLOWED_RESUBMIT_STATES, workState)) {
		return { visible: false };
	}
	if (workState === CLAIM_STATES.closed) {
		return { visible: true, disabled: true, tooltip: REOPEN_CLAIM_TOOLTIP };
	}
	return { visible: true, disabled: false };
};

const getFileSecondaryClaim = (workState: ClaimRequest.IClaimWorkState): ActionState => {
	if (!_.includes(ALLOWED_SECONDARY_FILING_STATES, workState)) {
		return { visible: false };
	}
	if (workState === CLAIM_STATES.closed) {
		return { visible: true, disabled: true, tooltip: REOPEN_CLAIM_TOOLTIP };
	}
	return { visible: true, disabled: false };
};

const ALLOWED_REFRESH_CLAIM_STATUS_STATES: ClaimRequest.IClaimWorkState[] = [
	CLAIM_STATES.processing,
	CLAIM_STATES.infoReq,
	CLAIM_STATES.adjudicated,
	CLAIM_STATES.paymentPosted,
	CLAIM_STATES.closed,
];

const getRefreshClaimStatus = (workState: ClaimRequest.IClaimWorkState): ActionState => {
	return { visible: _.includes(ALLOWED_REFRESH_CLAIM_STATUS_STATES, workState) };
};

const getPostPayment = ({
	workState,
	hasManualClaim,
	isDraftPostAvailable,
}: {
	workState: ClaimRequest.IClaimWorkState;
	hasManualClaim?: boolean;
	isDraftPostAvailable?: boolean;
}): PostPaymentActionState => {
	if ([CLAIM_STATES.superseded, CLAIM_STATES.notAccepted, CLAIM_STATES.void, CLAIM_STATES.archived].includes(workState)) {
		return { visible: false };
	}
	const isPostedWithDraft = workState === CLAIM_STATES.paymentPosted && !!isDraftPostAvailable;
	const location: PostPaymentActionState['location'] = hasManualClaim && !isPostedWithDraft ? 'dropdown' : 'page';
	if (workState === CLAIM_STATES.closed) {
		return { visible: true, disabled: true, location, tooltip: REOPEN_CLAIM_TOOLTIP };
	}
	if (workState === CLAIM_STATES.paymentPosted) {
		return { visible: true, disabled: false, location };
	}
	return { visible: true, disabled: false, location };
};

const getAddActivity = (workState: ClaimRequest.IClaimWorkState, userAccess?: App.UserAccess): ActionState => {
	// only Billing Admin, Super Admin, and Billing User can add an activity
	if (userAccess && !(userAccess?.isBillingAdmin || userAccess?.isSuperAdmin || userAccess?.isBillingUser)) {
		return { visible: true, disabled: true, tooltip: 'You do not have access to add an activity' };
	}
	if (workState === CLAIM_STATES.notAccepted) {
		return { visible: true, disabled: true, tooltip: 'Actions are disabled because this claim was not accepted' };
	}
	if (workState === CLAIM_STATES.superseded || workState === CLAIM_STATES.voidSuperseded) {
		return { visible: true, disabled: true, tooltip: 'Actions are disabled as this claim has been superseded.' };
	}
	return { visible: true, disabled: false };
};

const getModifyClaimWorklist = (workState: ClaimRequest.IClaimWorkState): ActionState => {
	if (_.includes([CLAIM_STATES.superseded, CLAIM_STATES.notAccepted], workState)) {
		return { visible: false, disabled: false };
	}
	return { visible: true, disabled: false };
};

const getAllActions = (workState: ClaimRequest.IClaimWorkState): ActionState => {
	if (workState === CLAIM_STATES.superseded) {
		return { visible: true, disabled: true, tooltip: 'Actions are disabled as this claim has been superseded.' };
	}
	if (workState === CLAIM_STATES.notAccepted) {
		return { visible: true, disabled: true, tooltip: 'Actions are disabled because this claim was not accepted.' };
	}
	return { visible: true, disabled: false };
};

const getClaimActions = ({ workState, pendingPR, hasManualClaim = false, userAccess, isDraftPostAvailable }: GetActionsParams): Actions => {
	return {
		changeWorkState: getChangeWorkState(workState, pendingPR, isDraftPostAvailable),
		resubmitClaim: getResubmitClaim(workState),
		postPayment: getPostPayment({ workState, hasManualClaim, isDraftPostAvailable }),
		addActivity: getAddActivity(workState, userAccess),
		modifyClaimWorklist: getModifyClaimWorklist(workState),
		refreshClaimStatus: { visible: true, disabled: false },
		allActions: getAllActions(workState),
		fileSecondaryClaim: getFileSecondaryClaim(workState),
	};
};

// States where payment-related amounts should be displayed (vs. shown as hyphen)
const AMOUNT_VISIBLE_WORK_STATES: ClaimRequest.IClaimWorkState[] = [CLAIM_STATES.paymentPosted, CLAIM_STATES.closed];

const shouldShowClaimAmount = (workState?: ClaimRequest.IClaimWorkState): boolean => {
	if (!workState) {
		return true;
	}
	return _.includes(AMOUNT_VISIBLE_WORK_STATES, workState);
};

export { getClaimActions, getPostPayment, getResubmitClaim, getRefreshClaimStatus, getModifyClaimWorklist, shouldShowClaimAmount };
